import { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2, Lock } from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { useIsAuthenticated } from '@/core/auth/SessionStore'
import { PickerSheet } from '@/features/common/PickerSheet'
import type { MveParteDto } from '@/types/parteDiariaDtos'
import { contextoEpp, contextoMve, rotuloMve, useParteDiariaStore } from '@/features/parteDiaria/parteDiariaStore'
import { useContextoParteDiaria, useMvesParteDiaria } from '@/features/parteDiaria/useParteDiaria'
import { carregarApoioOffline } from '@/features/parteDiaria/parteDiariaOffline'
import { dataBR, datasPermitidas, hojeSP } from '@/features/parteDiaria/parteDiariaRegras'
import { Alerta, PickerField } from '@/features/parteDiaria/fields'
import { fmtBRL } from '@/features/parteDiaria/format'

/**
 * Passo 0: data (hoje, fixa) → equipamento do mês (lista A.1 ∪ A.2 do chefe)
 * → contexto do SIGA (contrato, transferência, bloqueio de hoje, horímetro).
 */
export function ParteDiariaStep0Route() {
  const authed = useIsAuthenticated()
  const navigate = useNavigate()
  const store = useParteDiariaStore()
  const [picker, setPicker] = useState(false)

  // O app lança ontem e hoje. Rascunho de data mais antiga não vale mais: volta para hoje.
  const hoje = hojeSP()
  const datas = datasPermitidas(hoje)
  useEffect(() => {
    if (!datas.includes(store.dt)) store.setDt(hoje)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // EPP é por período (já aceita qualquer dia do mês até hoje): a referência é sempre hoje.
  useEffect(() => {
    if (store.modulo === 'EPP' && store.dt !== hoje) store.setDt(hoje)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.modulo, store.dt])

  const mves = useMvesParteDiaria(store.dt)
  const ctx = useContextoParteDiaria(store.mveId, store.dt)
  useEffect(() => {
    if (ctx.status === 'live' || ctx.status === 'cache') store.setContexto(ctx.contexto, ctx.status)
    else if (ctx.status === 'offline' || ctx.status === 'erro') store.setContexto(null, null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.status, ctx.contexto])

  // Resumo do pacote offline + outbox local: dá horímetro inicial e modo do
  // dia para QUALQUER MVE do chefe, mesmo sem contexto em cache.
  useEffect(() => {
    if (store.mveId == null) return
    let cancelado = false
    void carregarApoioOffline(store.mveId, store.dt).then((a) => {
      if (!cancelado) store.setApoio(a)
    })
    return () => {
      cancelado = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.mveId, store.dt])

  const itens = useMemo(() => {
    const ordem = { 'M+C': 0, M: 1, E: 2 } as const
    return [...mves.items].sort((a, b) => ordem[a.tipo] - ordem[b.tipo] || (a.prefixo ?? '').localeCompare(b.prefixo ?? ''))
  }, [mves.items])

  if (!authed) return <Navigate to="/login" replace />

  const cMve = contextoMve(store)
  const cEpp = contextoEpp(store)
  const bloqueios = store.contexto?.bloqueios ?? []
  const diaBloqueado = cMve?.dia.modo === 'BLOQUEADO' || (cEpp != null && !cEpp.podeInserir)
  const carregando = ctx.status === 'loading'
  const podeContinuar =
    store.mveId != null && !carregando && bloqueios.length === 0 && !diaBloqueado && ctx.recusa == null

  return (
    <main className="min-h-svh bg-bg-base flex flex-col">
      <header className="sticky top-0 z-10 bg-bg-base/95 backdrop-blur border-b border-border pt-safe pl-safe pr-safe">
        <div className="flex items-center gap-2 px-2 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/parte-diaria')} aria-label="Voltar">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-bold text-on-surface">Parte Diária · 1 de 3 · Equipamento</h1>
        </div>
      </header>

      <div className="flex-1 p-4 space-y-3 max-w-2xl mx-auto w-full">
        <div>
          <p className="text-[11px] font-semibold text-on-surface-dim uppercase tracking-[0.08em] px-1 mb-1.5">Data do lançamento</p>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Data do lançamento">
            {datas.map((d) => {
              const ativo = store.dt === d
              const bloqueadoEpp = store.modulo === 'EPP' && d !== hoje
              return (
                <button
                  key={d}
                  type="button"
                  role="radio"
                  aria-checked={ativo}
                  disabled={bloqueadoEpp}
                  onClick={() => store.setDt(d)}
                  className={`h-14 rounded-lg border text-left px-3 transition-colors disabled:opacity-40 ${
                    ativo ? 'border-accent bg-accent/10 text-on-surface' : 'border-border bg-bg-panel text-on-surface-dim'
                  }`}
                >
                  <span className="block text-[11px] font-semibold uppercase tracking-wide">{d === hoje ? 'Hoje' : 'Ontem'}</span>
                  <span className="block text-sm font-mono tabular">{dataBR(d)}</span>
                </button>
              )
            })}
          </div>
          <p className="text-xs text-on-surface-dim px-1 mt-1.5">
            {store.modulo === 'EPP'
              ? 'Equipamento de pequeno porte é lançado por período; escolha as datas no próximo passo.'
              : 'O app lança o dia de hoje e o dia anterior.'}
          </p>
        </div>

        <PickerField
          label="EQUIPAMENTO"
          value={store.mveId != null ? rotuloMve(store) : 'Selecionar…'}
          badge={store.mveTipo === 'E' ? 'EPP' : store.mveTipo}
          onClick={() => setPicker(true)}
        />
        {mves.loading && (
          <p className="text-xs text-on-surface-dim px-1 inline-flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" /> Buscando equipamentos do chefe no mês…
          </p>
        )}
        {mves.recusa && <Alerta tone="bad">{mves.recusa}</Alerta>}
        {!mves.loading && mves.source === 'cache' && (
          <p className="text-xs text-warn px-1">Sem rede — lista de equipamentos em cache. Será validada no envio.</p>
        )}
        {!mves.loading && mves.source == null && mves.error && (
          <p className="text-xs text-warn px-1">Sem rede e sem cache de equipamentos deste mês. Conecte e tente novamente.</p>
        )}

        {store.mveId != null && carregando && (
          <p className="text-xs text-on-surface-dim px-1 inline-flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" /> Carregando contexto do SIGA…
          </p>
        )}
        {ctx.recusa && <Alerta tone="bad">{ctx.recusa.message}</Alerta>}
        {ctx.message && !ctx.recusa && <p className="text-xs text-warn px-1">{ctx.message}</p>}
        {bloqueios.length > 0 && (
          <Alerta tone="bad">
            {bloqueios.map((b) => (
              <span key={b.code} className="block">
                {b.message}
              </span>
            ))}
          </Alerta>
        )}
        {(store.contexto?.avisos.length ?? 0) > 0 && (
          <Alerta tone="warn">
            {store.contexto!.avisos.map((a) => (
              <span key={a} className="block">
                {a}
              </span>
            ))}
          </Alerta>
        )}

        {cMve?.dia.modo === 'BLOQUEADO' && (
          <Alerta tone="bad">
            <span className="inline-flex items-start gap-2">
              <Lock className="h-4 w-4 shrink-0 mt-0.5" /> {cMve.dia.message}
            </span>
          </Alerta>
        )}

        {cEpp && (
          <div className="card-surface p-4 space-y-2">
            <h2 className="text-xs font-bold text-accent uppercase">Períodos do mês</h2>
            {!cEpp.podeInserir && (
              <p className="text-sm text-bad inline-flex items-start gap-2">
                <Lock className="h-4 w-4 shrink-0 mt-0.5" /> {cEpp.message}
              </p>
            )}
            {cEpp.linhasMes.length === 0 ? (
              <p className="text-sm text-on-surface-dim">Nenhum período lançado neste mês.</p>
            ) : (
              cEpp.linhasMes.map((l) => (
                <Row key={l.sigaEppId} label={`${dataBR(l.dtInicial)} a ${dataBR(l.dtFinal)}`} value={`${l.obraCodigo ?? ''} · ${fmtBRL(l.valor)}`} />
              ))
            )}
            <Row label="Valor realizado (mês)" value={fmtBRL(cEpp.valorRealizadoMes)} />
          </div>
        )}
      </div>

      <div className="sticky bottom-0 bg-bg-base/95 backdrop-blur border-t border-border p-4 pb-safe-plus-4 pl-safe pr-safe">
        <Button variant="primary" size="lg" disabled={!podeContinuar} onClick={() => navigate('/parte-diaria/step1')} className="w-full">
          Continuar
        </Button>
      </div>

      {picker && (
        <PickerSheet<MveParteDto>
          open
          title="Equipamento do mês"
          items={itens}
          label={(m) => `${m.prefixo ?? '(sem prefixo)'} · ${m.tipo === 'E' ? 'EPP' : m.tipo}`}
          sublabel={(m) =>
            [
              [m.marca, m.modelo].filter(Boolean).join(' '),
              m.medicaoGerada ? 'medição gerada' : m.qtdMovimentacao > 0 ? `${m.qtdMovimentacao} lançamento(s)` : 'sem lançamento',
            ]
              .filter(Boolean)
              .join(' · ') || null
          }
          onPick={(m) => {
            store.setMve(m)
            setPicker(false)
          }}
          onDismiss={() => setPicker(false)}
          searchHint="Buscar por prefixo, marca ou estado"
          emptyMessage={
            mves.loading
              ? 'Carregando…'
              : mves.source == null
                ? 'Sem rede e sem cache de equipamentos. Conecte e tente novamente.'
                : 'Nenhum equipamento com contrato MVE/EPP vigente para o chefe neste mês.'
          }
          header={
            mves.source === 'cache' ? <div className="px-4 py-2 text-xs text-warn">Sem rede — lista em cache.</div> : null
          }
        />
      )}
    </main>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-on-surface-dim">{label}</span>
      <span className="text-on-surface text-right">{value || '—'}</span>
    </div>
  )
}
